import React from 'react';
import { IconButton, Paper, Theme, Tooltip, styled } from '@mui/material';
import Icon from '@mdi/react';
import { mdiVectorPolyline } from '@mdi/js';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { bbox } from '@turf/turf';

import { Lane } from '../../interfaces/services/lanes';
import {
    LessFreightRecommendation,
    MoreFreightRecommendation,
    NetworkRecommendations,
    Recommendation,
    StartServiceRecommendation,
} from '../../interfaces/services/networkRecommendations';
import { formatNumber, formatRecommendationType } from '../../utils/formattingUtils';
import { RecommendationType } from '../../utils/enums';
import { FeatureTypes, MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleLineStringPopupWithDescriptionMouseEnter,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    toggleLayerVisibility,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import {
    addLaneCenterLayer,
    addLaneLineLayer,
    addLaneLineHoverLayer,
    addNetworkRecommendationLaneCenterLayer,
    addNetworkRecommendationLaneLineHoverLayer,
    addNetworkRecommendationLaneLineLayer,
    addSelectedRecommendationLaneCenterLayer,
    addSelectedRecommendationLaneLineHoverLayer,
    addSelectedRecommendationLaneLineLayer,
} from '../../utils/maps/layers/networkRx';
import { addNetworkRxSource, NetworkRxMapSourceProperties } from '../../utils/maps/sources/networkRx';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'laneMap';

const classes = {
    map: `${classesPrefix}-map`,
    paperWrapper: `${classesPrefix}-paperWrapper`,
    controlsRight: `${classesPrefix}-controlsRight`,
    controlButton: `${classesPrefix}-controlButton`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
        [`& .${classes.paperWrapper}`]: {
            display: 'inline-grid',
            borderRadius: '2px',
        },
        [`& .${classes.controlsRight}`]: {
            position: 'absolute',
            right: '10px',
            top: '158px',
            zIndex: 10,
        },
        [`& .${classes.controlButton}`]: {
            padding: '4px',
        },
    };
});

interface NetworkRxMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Selected recommendation types to show on map */
    selectedRecommendationType: RecommendationType;
    /** Selected recommendation to highlight on map */
    selectedRecommendations: (Recommendation | MoreFreightRecommendation | LessFreightRecommendation | StartServiceRecommendation)[];
    /** Recommendations to add to the map */
    networkRecommendations: NetworkRecommendations;
    /** Lanes to add to the map */
    lanes: Lane[];
    /** Hovered lane id to highlight */
    hoveredLaneId: string | null;
    /** Function to handle hovering a lane */
    handleHoveredLaneChange: (laneId: string | null) => void;
    /** Function to handle clicking a lane */
    handleLaneClick: (laneId: string) => void;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface NetworkRxMapState {
    mapStyleLoaded: boolean;
    isNetworkActive: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class NetworkRxMap extends React.Component<NetworkRxMapProps, NetworkRxMapState> {

    constructor(props: NetworkRxMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
            isNetworkActive: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeNetworkRxEventHandlers();
            this.addNetworkRecommendationsToMap();

            map.setFilter(MapLayers.networkRecommendationLaneLineLayer, ['all',
                ['==', '$type', 'LineString'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);
            map.setFilter(MapLayers.networkRecommendationLaneLineHoverLayer, ['all',
                ['==', '$type', 'LineString'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);
            map.setFilter(MapLayers.networkRecommendationLaneCenterLayer, ['all',
                ['==', '$type', 'Point'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneCenter],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);

            // toggle visibility on existing network lanes
            toggleLayerVisibility(map, MapLayers.laneLineLayer, this.state.isNetworkActive);
            toggleLayerVisibility(map, MapLayers.laneLineHoverLayer, this.state.isNetworkActive);
            toggleLayerVisibility(map, MapLayers.laneCenterLayer, this.state.isNetworkActive);

            // toggle visibility on all recommendations based on if one is selected
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneLineLayer, this.props.selectedRecommendations.length === 0);
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneLineHoverLayer, this.props.selectedRecommendations.length === 0);
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneCenterLayer, this.props.selectedRecommendations.length === 0);

            // toggle visibility on selected recommendation layer based on if one is selected
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneLineLayer, this.props.selectedRecommendations.length > 0);
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneLineHoverLayer, this.props.selectedRecommendations.length > 0);
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneCenterLayer, this.props.selectedRecommendations.length > 0);

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: NetworkRxMapProps, prevState: NetworkRxMapState): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        if (prevProps.selectedRecommendationType !== this.props.selectedRecommendationType) {
            map.setFilter(MapLayers.networkRecommendationLaneLineLayer, ['all',
                ['==', '$type', 'LineString'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);
            map.setFilter(MapLayers.networkRecommendationLaneLineHoverLayer, ['all',
                ['==', '$type', 'LineString'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);
            map.setFilter(MapLayers.networkRecommendationLaneCenterLayer, ['all',
                ['==', '$type', 'Point'],
                ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneCenter],
                ['==', 'recommendationType', this.props.selectedRecommendationType],
            ]);
        }

        // toggle lane line visibility
        if (prevState.isNetworkActive !== this.state.isNetworkActive) {
            toggleLayerVisibility(map, MapLayers.laneLineLayer, this.state.isNetworkActive);
            toggleLayerVisibility(map, MapLayers.laneLineHoverLayer, this.state.isNetworkActive);
            toggleLayerVisibility(map, MapLayers.laneCenterLayer, this.state.isNetworkActive);
        }

        if (prevProps.hoveredLaneId !== this.props.hoveredLaneId) {
            this.addNetworkRecommendationsSourceToMap();
        }

        // add networkRecommendations to map if props have updated
        if (prevProps.networkRecommendations !== this.props.networkRecommendations ||
            prevProps.lanes !== this.props.lanes ||
            prevState.isNetworkActive !== this.state.isNetworkActive ||
            prevProps.selectedRecommendationType !== this.props.selectedRecommendationType ||
            prevProps.selectedRecommendations !== this.props.selectedRecommendations) {
            this.removeNetworkRxEventHandlers();
            this.addNetworkRecommendationsToMap();

            // toggle visibility on all recommendations based on if one is selected
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneLineLayer, this.props.selectedRecommendations.length === 0);
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneLineHoverLayer, this.props.selectedRecommendations.length === 0);
            toggleLayerVisibility(map, MapLayers.networkRecommendationLaneCenterLayer, this.props.selectedRecommendations.length === 0);

            // toggle visibility on selected recommendation layer based on if one is selected
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneLineLayer, this.props.selectedRecommendations.length > 0);
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneLineHoverLayer, this.props.selectedRecommendations.length > 0);
            toggleLayerVisibility(map, MapLayers.selectedRecommendationLaneCenterLayer, this.props.selectedRecommendations.length > 0);
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeNetworkRxEventHandlers();
    }

    handleLaneLineLayerMouseenter = (e: any): void => {
        if (map) {
            // Get the layer that was entered
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the hover function
            if (currentFeature.layer.id === MapLayers.laneLineHoverLayer) {
                if (e.features.length > 0) {
                    const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];

                    // update the hovered lane id
                    this.props.handleHoveredLaneChange(properties.id);

                    // show the line popup
                    handleLineStringPopupWithDescriptionMouseEnter(map, e, properties.description, trimbleMapsPopup);
                }
            }
        }
    };

    handleNetworkRecommendationLaneLineLayerMouseenter = (e: any): void => {
        if (map) {
            if (e.features.length > 0) {
                const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];

                // update the hovered lane id
                this.props.handleHoveredLaneChange(properties.id);
            }

            // Get the features for the layer that was entered
            const features = map.queryRenderedFeatures(e.point);

            if (features.length > 0) {
                // Because features come from tiled vector data, feature geometries may be split or duplicated across tile boundaries.
                // As a result, features may appear multiple times in query results.
                const uniqueIds = new Set();
                const uniqueFeatures: TrimbleMaps.GeoJSONFeature[] = [];
                features.forEach((feature) => {
                    const id = feature.properties?.recommendationLaneId;
                    if (!uniqueIds.has(id)) {
                        uniqueIds.add(id);
                        uniqueFeatures.push(feature);
                    }
                });

                // Using the unique list of features, group the list by lane names
                const consolidatedFeatures = uniqueFeatures.reduce((accumulator: Record<string, GeoJSON.Feature<GeoJSON.LineString, NetworkRxMapSourceProperties>[]>, currentFeature) => {
                    if (currentFeature.layer.id === MapLayers.networkRecommendationLaneLineHoverLayer) {
                        const properties = currentFeature.properties as NetworkRxMapSourceProperties;

                        const key = `${properties.pickupServiceAreaName} -> ${properties.deliveryServiceAreaName}`;
                        if (!accumulator[key]) {
                            accumulator[key] = [];
                        }
                        // @ts-ignore
                        accumulator[key].push(currentFeature);
                        return accumulator;
                    }

                    return accumulator;
                }, {});

                if (Object.values(consolidatedFeatures).length > 0) {
                    const recommendationDetailsList = Object.entries(consolidatedFeatures).map(([laneId, featureList]) => {
                        const recommendationDetails = featureList.map((feature) => {
                            const { properties } = feature;

                            if (properties.recommendationType === RecommendationType.MoveMoreFreight || properties.recommendationType === RecommendationType.MoveLessFreight) {
                                return `
                                    <div class='mapTooltipRow'>
                                        <div class='mapTooltipCell'>${properties.targetPickupPartyName}</div>
                                        <div class='mapTooltipCell'><strong>${formatNumber(properties.freightOrderOriginal, 0, 0)} orders</strong></div>
                                    </div>
                                `;
                            }

                            return '';
                        });

                        return `
                            <div class='mapTooltipRow'>
                                <strong>${laneId}</strong>
                            </div>
                            ${recommendationDetails.join('')}
                        `;
                    });

                    const tooltipHtml = `
                        <div class='mapTooltip'>
                            <div class='mapTooltipRow'>
                                <u>${formatRecommendationType(Object.values(consolidatedFeatures)[0][0].properties.recommendationType)}</u>
                            </div>
                            ${recommendationDetailsList.join('')}
                        </div>
                    `;

                    // show the line popup
                    handleLineStringPopupWithDescriptionMouseEnter(map, e, tooltipHtml, trimbleMapsPopup);
                }
            }
        }
    };

    handleSelectedRecommendationLaneLineLayerMouseenter = (e: any): void => {
        if (map) {
            // Get the layer that was entered
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the hover function
            if (currentFeature.layer.id === MapLayers.selectedRecommendationLaneLineHoverLayer) {
                if (e.features.length > 0) {
                    const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];

                    // update the hovered lane id
                    this.props.handleHoveredLaneChange(properties.id);

                    // show the line popup
                    handleLineStringPopupWithDescriptionMouseEnter(map, e, properties.description, trimbleMapsPopup);
                }
            }
        }
    };

    handleLaneLineLayerMouseleave = (): void => {
        if (map) {
            // reset the hovered lane id
            this.props.handleHoveredLaneChange(null);

            // remove the line popup
            handlePopupMouseLeave(map, trimbleMapsPopup);
        }
    };

    handleLaneLineLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.laneLineHoverLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];
                // handle action from parent component
                this.props.handleLaneClick(properties.id);
            }
        }
    };

    handleNetworkRecommendationLaneLineLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.networkRecommendationLaneLineHoverLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];

                // Allow the click if the recommendation type isn't Start Service
                if (properties.recommendationType !== RecommendationType.StartService) {
                    // handle action from parent component
                    this.props.handleLaneClick(properties.id);
                }
            }
        }
    };

    handleSelectedRecommendationLaneLineLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.selectedRecommendationLaneLineHoverLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: NetworkRxMapSourceProperties; } = e.features[0];

                // Allow the click if the recommendation type isn't Start Service
                if (properties.recommendationType !== RecommendationType.StartService) {
                    // handle action from parent component
                    this.props.handleLaneClick(properties.id);
                }
            }
        }
    };

    addNetworkRecommendationsSourceToMap = (): void => {
        if (map) {
            // add map source
            addNetworkRxSource({
                map,
                networkRecommendations: this.props.networkRecommendations,
                selectedRecommendations: this.props.selectedRecommendations,
                lanes: this.props.lanes,
                hoveredLaneId: this.props.hoveredLaneId,
                theme: this.props.theme,
            });
        }
    };

    addNetworkRecommendationsToMap = (): void => {
        if (map) {
            const networkRxSource = addNetworkRxSource({
                map,
                networkRecommendations: this.props.networkRecommendations,
                selectedRecommendations: this.props.selectedRecommendations,
                lanes: this.props.lanes,
                hoveredLaneId: this.props.hoveredLaneId,
                theme: this.props.theme,
            });

            // add lane lines
            addLaneLineLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.state.isNetworkActive,
            });

            // add lane lines hover
            addLaneLineHoverLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.state.isNetworkActive,
            });
            map.on('mouseenter', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseenter);
            map.on('mouseleave', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseleave);
            map.on('click', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerClick);

            // add lane centers
            addLaneCenterLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.state.isNetworkActive,
            });
            map.on('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            // add network recommendation lane lines
            addNetworkRecommendationLaneLineLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length === 0,
                selectedRecommendationType: this.props.selectedRecommendationType,
            });

            // add network recommendation lane lines hover
            addNetworkRecommendationLaneLineHoverLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length === 0,
                selectedRecommendationType: this.props.selectedRecommendationType,
            });
            map.on('mouseenter', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleNetworkRecommendationLaneLineLayerMouseenter);
            map.on('mouseleave', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleLaneLineLayerMouseleave);
            map.on('click', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleNetworkRecommendationLaneLineLayerClick);

            // add network recommendation lane centers
            addNetworkRecommendationLaneCenterLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length === 0,
                selectedRecommendationType: this.props.selectedRecommendationType,
            });
            map.on('mouseenter', MapLayers.networkRecommendationLaneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.networkRecommendationLaneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            // add selected recommendation lane lines
            addSelectedRecommendationLaneLineLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length > 0,
            });

            // add selected recommendation lane lines hover
            addSelectedRecommendationLaneLineHoverLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length > 0,
            });
            map.on('mouseenter', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleSelectedRecommendationLaneLineLayerMouseenter);
            map.on('mouseleave', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleLaneLineLayerMouseleave);
            map.on('click', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleSelectedRecommendationLaneLineLayerClick);

            // add selected recommendation lane centers
            addSelectedRecommendationLaneCenterLayer({
                map,
                mapSource: MapSources.networkRecommendationsSource,
                isVisible: this.props.selectedRecommendations.length > 0,
            });
            map.on('mouseenter', MapLayers.selectedRecommendationLaneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.selectedRecommendationLaneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const { data } = networkRxSource;
            const { features } = data as GeoJSON.FeatureCollection<GeoJSON.Point | GeoJSON.LineString, NetworkRxMapSourceProperties>;
            if (features.length > 0) {
                // determine the visible features only by using all of the above criteria used on the source and layers
                const visibleFeatures = features.reduce((featureList: GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, NetworkRxMapSourceProperties>[], currentFeature) => {
                    // if recommendation feature and there is not one currently selected (i.e. visible) and it's the current selected recommendation type, push to feature list
                    if ((currentFeature.properties.featureType === FeatureTypes.NetworkRecommendationLaneCenter ||
                        currentFeature.properties.featureType === FeatureTypes.NetworkRecommendationLaneLine) &&
                        this.props.selectedRecommendations.length === 0 &&
                        currentFeature.properties.recommendationType === this.props.selectedRecommendationType) {
                        featureList.push(currentFeature);
                    }

                    // if selected recommendation feature and the selected recommendation is not null (i.e. visible), push to feature list
                    if ((currentFeature.properties.featureType === FeatureTypes.SelectedRecommendationLaneCenter ||
                        currentFeature.properties.featureType === FeatureTypes.SelectedRecommendationLaneLine) &&
                        this.props.selectedRecommendations.length > 0) {
                        featureList.push(currentFeature);
                    }

                    return featureList;
                }, []);

                if (visibleFeatures.length > 0) {
                    const boundingBox = bbox({
                        type: 'FeatureCollection',
                        features: visibleFeatures,
                    });
                    fitMapToBounds({
                        map,
                        bounds: boundingBox as LngLatBoundsLike,
                        zoomMap: true,
                        maxZoom: 12,
                    });
                }
            }
        }
    };

    removeNetworkRxEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.laneLineHoverLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseenter);
                map.off('mouseleave', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseleave);
                map.off('click', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerClick);
            }

            if (map.getLayer(MapLayers.laneCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }

            if (map.getLayer(MapLayers.networkRecommendationLaneLineHoverLayer) !== undefined) {
                map.off('mouseenter', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleNetworkRecommendationLaneLineLayerMouseenter);
                map.off('mouseleave', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleLaneLineLayerMouseleave);
                map.off('click', MapLayers.networkRecommendationLaneLineHoverLayer, this.handleNetworkRecommendationLaneLineLayerClick);
            }

            if (map.getLayer(MapLayers.networkRecommendationLaneCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.networkRecommendationLaneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.networkRecommendationLaneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }

            if (map.getLayer(MapLayers.selectedRecommendationLaneLineHoverLayer) !== undefined) {
                map.off('mouseenter', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleNetworkRecommendationLaneLineLayerMouseenter);
                map.off('mouseleave', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleLaneLineLayerMouseleave);
                map.off('click', MapLayers.selectedRecommendationLaneLineHoverLayer, this.handleSelectedRecommendationLaneLineLayerClick);
            }

            if (map.getLayer(MapLayers.selectedRecommendationLaneCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.selectedRecommendationLaneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.selectedRecommendationLaneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='networkRx-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
                <Paper elevation={2} className={`${classes.paperWrapper} ${classes.controlsRight}`}>
                    <Tooltip title={this.state.isNetworkActive ? 'Hide Existing Network' : 'Show Existing Network'}>
                        <IconButton
                            className={classes.controlButton}
                            onClick={(): void => {
                                this.setState((state) => {
                                    return {
                                        isNetworkActive: !state.isNetworkActive,
                                    };
                                });
                            }}
                        >
                            <Icon
                                path={mdiVectorPolyline}
                                size={1}
                                color={this.state.isNetworkActive ? this.props.theme.palette.info.main : this.props.theme.palette.text.primary}
                            />
                        </IconButton>
                    </Tooltip>
                </Paper>
            </StyledDiv>
        );
    }

}

export default NetworkRxMap;
